import { commentRootOf } from '@nostr-post/core/comments';
import { filterLatestAddressableEvents } from '@nostr-post/core/nip33';
import {
  type NostrPostManifest,
  STANDARD_KIND1_POST_MANIFEST,
  type UnsignedNostrEvent,
} from '@nostr-post/core/types';
import type { SignedEvent } from '@nostr-post/signer';
import type { ReplyTarget } from './replies';

/** An addressable event's address (`kind:pubkey:d`); undefined for other events */
export const addressOf = (event: SignedEvent): string | undefined => {
  const dTag = event.tags.find((tag) => tag[0] === 'd')?.[1];
  return event.kind >= 30000 && event.kind < 40000 && dTag !== undefined
    ? `${event.kind}:${event.pubkey}:${dTag}`
    : undefined;
};

export interface FeedThread {
  root: SignedEvent;
  replies: SignedEvent[];
  reactions: SignedEvent[];
}

export const DEFAULT_REACTION_OPTIONS = ['+', '❤️', '🔥', '😂'];

export const DEFAULT_COMMENT_MANIFEST: NostrPostManifest = STANDARD_KIND1_POST_MANIFEST;

const getFirstTagValue = (event: SignedEvent, tagName: string): string | undefined => {
  return event.tags.find((tag) => tag[0] === tagName)?.[1];
};

const getRootTagValue = (event: SignedEvent): string | undefined => {
  const rootTag = event.tags.find((tag) => tag[0] === 'e' && tag[3] === 'root');
  if (rootTag?.[1]) return rootTag[1];
  return getFirstTagValue(event, 'e');
};

const isReactionEvent = (event: SignedEvent): boolean => event.kind === 7;

/** A kind-1 reply or a NIP-22 comment (`commentRootOf`) */
const isReplyEvent = (event: SignedEvent): boolean => commentRootOf(event) !== undefined;

const insertUniqueEvent = (events: SignedEvent[], nextEvent: SignedEvent): SignedEvent[] => {
  if (events.some((event) => event.id === nextEvent.id)) return events;
  return [nextEvent, ...events];
};

const sortByCreatedAtDescending = (left: SignedEvent, right: SignedEvent) => {
  return right.created_at - left.created_at;
};

const sortByCreatedAtAscending = (left: SignedEvent, right: SignedEvent) => {
  return left.created_at - right.created_at;
};

export const mergeUniqueEvents = (
  events: SignedEvent[],
  nextEvents: SignedEvent[]
): SignedEvent[] => {
  return nextEvents.reduce(insertUniqueEvent, events).sort(sortByCreatedAtDescending);
};

export const buildReplyTarget = (event: SignedEvent, rootEvent?: SignedEvent): ReplyTarget => {
  const commentRoot = commentRootOf(event);
  const resolvedRootId = commentRoot?.id ?? rootEvent?.id ?? event.id;
  const rootAddress = addressOf(rootEvent ?? event) ?? commentRoot?.address;
  return {
    replyToEventId: event.id,
    replyToPubkey: event.pubkey,
    rootEventId: resolvedRootId,
    rootPubkey: rootEvent?.pubkey ?? (resolvedRootId === event.id ? event.pubkey : undefined),
    ...(rootAddress ? { rootAddress } : {}),
  };
};

/**
 * A reaction (NIP-25, kind 7) to `event`: `e` and `p`, the reacted event's kind (`k`) and, for an
 * addressable event, its address (`a`), so the reaction stays with it across its versions
 */
export const buildReactionEvent = (
  event: SignedEvent,
  reaction: string,
  pubkey: string
): UnsignedNostrEvent => {
  const address = addressOf(event);
  return {
    kind: 7,
    created_at: Math.floor(Date.now() / 1000),
    pubkey,
    content: reaction,
    tags: [
      ['e', event.id],
      ['p', event.pubkey],
      ...(address ? [['a', address] as [string, string]] : []),
      ['k', String(event.kind)],
    ],
  };
};

/** Adds `event` to the list under `key`, once */
const addOnce = (
  lists: Map<string, SignedEvent[]>,
  key: string | undefined,
  event: SignedEvent
): void => {
  if (!key) return;
  const list = lists.get(key) ?? [];
  if (!list.some((e) => e.id === event.id)) list.push(event);
  lists.set(key, list);
};

/** The current version a reaction is for: its `e` event, else the event its `a` address names now */
const reactionTargetId = (
  reaction: SignedEvent,
  primaryIds: ReadonlySet<string>,
  rootIdByAddress: ReadonlyMap<string, string>
): string | undefined => {
  const id = getFirstTagValue(reaction, 'e');
  if (id && primaryIds.has(id)) return id;
  const address = getFirstTagValue(reaction, 'a');
  return (address && rootIdByAddress.get(address)) || id;
};

export const buildThreads = (
  primaryEvents: SignedEvent[],
  interactionEvents: SignedEvent[]
): FeedThread[] => {
  const dedupedPrimary = filterLatestAddressableEvents(primaryEvents);
  const allEvents = [...dedupedPrimary, ...interactionEvents];
  const primaryIds = new Set(dedupedPrimary.map((event) => event.id));
  // Replies to an earlier version of an addressable root reach it through its address
  const rootIdByAddress = new Map(
    dedupedPrimary.flatMap((event) => {
      const address = addressOf(event);
      return address ? [[address, event.id] as const] : [];
    })
  );
  const currentRootId = (reply: SignedEvent): string | undefined => {
    const root = commentRootOf(reply);
    if (root?.id && primaryIds.has(root.id)) return root.id;
    return root?.address ? rootIdByAddress.get(root.address) : undefined;
  };
  const repliesByRoot = new Map<string, SignedEvent[]>();
  const reactionsByTarget = new Map<string, SignedEvent[]>();

  for (const event of allEvents) {
    if (isReactionEvent(event)) {
      // A reaction to an earlier version of an addressable event counts for the current one
      addOnce(reactionsByTarget, reactionTargetId(event, primaryIds, rootIdByAddress), event);
    } else if (isReplyEvent(event)) {
      const rootId = currentRootId(event);
      if (rootId !== event.id) addOnce(repliesByRoot, rootId, event);
    }
  }

  const roots = dedupedPrimary
    .filter((event) => !isReactionEvent(event))
    .filter((event) => {
      const rootId = getRootTagValue(event);
      return !rootId || rootId === event.id || !primaryIds.has(rootId);
    })
    .sort(sortByCreatedAtDescending);

  return roots.map((root) => ({
    root,
    replies: (repliesByRoot.get(root.id) ?? []).sort(sortByCreatedAtAscending),
    reactions: (reactionsByTarget.get(root.id) ?? []).sort(sortByCreatedAtDescending),
  }));
};

export const summarizeReactions = (reactions: SignedEvent[]): Array<[string, number]> => {
  const counts = new Map<string, number>();

  for (const reaction of reactions) {
    const value = reaction.content.trim() || '+';
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
};
